import { useEffect, useState } from "react";
import { Platform, StyleSheet, Text, View } from "react-native";
import QRCode from "react-native-qrcode-svg";

import { getStaffAuthMessage } from "../lib/auth-contract";
import {
  completeStaffMagicLogin,
  isStaffMagicLoginLink,
  recordCentralStaffLoginAudit,
  requestStaffMagicLogin,
} from "../lib/staff-invitations";
import { useStaffAuth } from "../providers/StaffAuthProvider";
import { central, semantic, spacing } from "../theme/tokens";
import { Button, ErrorState, Field } from "./ui";

export function StaffSignInPanel() {
  const auth = useStaffAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [magicMode, setMagicMode] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicCompletionUrl, setMagicCompletionUrl] = useState<string | null>(
    null,
  );
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    try {
      if (isStaffMagicLoginLink(window.location.href)) {
        setMagicCompletionUrl(window.location.href);
        setMagicMode(true);
      }
    } catch {
      setLocalError(getStaffAuthMessage("auth/invalid-action-code"));
    }
  }, []);

  if (auth.status === "email-verification-required") {
    return (
      <View style={styles.panel}>
        <View style={styles.copy}>
          <Text accessibilityRole="header" style={styles.title}>
            Verify your email
          </Text>
          <Text style={styles.body}>
            Open the verification email we sent, then return here to sign in.
          </Text>
        </View>
        <Button
          fullWidth
          label="Send verification again"
          loading={auth.busy}
          onPress={() => void auth.resendVerification()}
          shape="rounded"
          variant="secondary"
        />
        <Button
          label="Use a different account"
          onPress={() => void auth.signOut()}
          shape="rounded"
          variant="link"
        />
      </View>
    );
  }

  if (auth.status === "unauthorized") {
    return (
      <View style={styles.panel}>
        <View style={styles.copy}>
          <Text accessibilityRole="header" style={styles.title}>
            Access not enabled
          </Text>
          <Text style={styles.body}>
            Ask your funeral home administrator to activate this account and
            assign its staff role.
          </Text>
        </View>
        <Button
          fullWidth
          label="Use a different account"
          onPress={() => void auth.signOut()}
          shape="rounded"
          variant="secondary"
        />
      </View>
    );
  }

  if (auth.status === "mfa-enrollment-required") {
    return (
      <View style={styles.panel}>
        <View style={styles.copy}>
          <Text accessibilityRole="header" style={styles.title}>
            Set up two-step verification
          </Text>
          <Text style={styles.body}>
            Use an authenticator app to protect your administrator account.
          </Text>
        </View>
        {auth.error ? (
          <ErrorState
            title="Verification was not completed"
            description={auth.error}
          />
        ) : null}
        {auth.mfaEnrollment ? (
          <>
            <View
              accessible
              accessibilityLabel="Authenticator setup QR code"
              style={styles.qrFrame}
            >
              <QRCode value={auth.mfaEnrollment.qrCodeUrl} size={176} />
            </View>
            <View style={styles.copy}>
              <Text style={styles.stepTitle}>1. Scan the code</Text>
              <Text style={styles.body}>
                Use Google Authenticator, 1Password, Authy, or another
                TOTP-compatible app.
              </Text>
              <Text selectable style={styles.secretKey}>
                {auth.mfaEnrollment.secretKey}
              </Text>
            </View>
            <View style={styles.copy}>
              <Text style={styles.stepTitle}>2. Enter the current code</Text>
              <Field
                autoComplete="one-time-code"
                keyboardType="number-pad"
                label="Verification code"
                maxLength={6}
                onChangeText={(value) => {
                  auth.clearError();
                  setMfaCode(value.replace(/\D/g, ""));
                }}
                onSubmitEditing={() => void auth.completeMfaEnrollment(mfaCode)}
                returnKeyType="done"
                spellCheck={false}
                inputStyle={styles.input}
                textContentType="oneTimeCode"
                value={mfaCode}
              />
              <Button
                disabled={mfaCode.length !== 6}
                fullWidth
                label="Finish setup"
                loading={auth.busy}
                onPress={() => void auth.completeMfaEnrollment(mfaCode)}
                shape="rounded"
              />
            </View>
          </>
        ) : (
          <Button
            fullWidth
            label="Set up authenticator"
            loading={auth.busy}
            onPress={() => void auth.beginMfaEnrollment()}
            shape="rounded"
          />
        )}
        <Button
          label="Sign out"
          onPress={() => void auth.signOut()}
          shape="rounded"
          variant="link"
        />
      </View>
    );
  }

  if (auth.status === "mfa-challenge-required") {
    return (
      <View style={styles.panel}>
        <View style={styles.copy}>
          <Text accessibilityRole="header" style={styles.title}>
            Enter your verification code
          </Text>
          <Text style={styles.body}>
            Open your authenticator app and enter the current six-digit code.
          </Text>
        </View>
        {auth.error ? (
          <ErrorState
            title="Verification was not completed"
            description={auth.error}
          />
        ) : null}
        <Field
          autoComplete="one-time-code"
          keyboardType="number-pad"
          label="Verification code"
          maxLength={6}
          onChangeText={(value) => {
            auth.clearError();
            setMfaCode(value.replace(/\D/g, ""));
          }}
          onSubmitEditing={() => void auth.completeMfaSignIn(mfaCode)}
          returnKeyType="done"
          spellCheck={false}
          inputStyle={styles.input}
          textContentType="oneTimeCode"
          value={mfaCode}
        />
        <Button
          disabled={mfaCode.length !== 6}
          fullWidth
          label="Verify"
          loading={auth.busy}
          onPress={() => void auth.completeMfaSignIn(mfaCode)}
          shape="rounded"
        />
        <Button
          label="Use a different account"
          onPress={() => void auth.signOut()}
          shape="rounded"
          variant="link"
        />
      </View>
    );
  }

  if (resetSent) {
    return (
      <View style={styles.panel}>
        <View accessibilityLiveRegion="polite" style={styles.copy}>
          <Text accessibilityRole="header" style={styles.title}>
            Check your email
          </Text>
          <Text style={styles.body}>
            If an active staff account matches that address, we sent password
            reset instructions.
          </Text>
        </View>
        <Button
          label="Back to Sign In"
          onPress={() => {
            setResetSent(false);
            setResetMode(false);
            auth.clearError();
          }}
          variant="secondary"
          shape="rounded"
        />
      </View>
    );
  }

  if (magicSent) {
    return (
      <View style={styles.panel}>
        <View accessibilityLiveRegion="polite" style={styles.copy}>
          <Text accessibilityRole="header" style={styles.title}>
            Check your email
          </Text>
          <Text style={styles.body}>
            If an active staff account matches that address, we sent a secure
            sign-in link. This link expires and can only be used once.
          </Text>
        </View>
        <Button
          label="Back to Sign In"
          onPress={() => {
            setMagicSent(false);
            setMagicMode(false);
            setLocalError(null);
            auth.clearError();
          }}
          shape="rounded"
          variant="secondary"
        />
      </View>
    );
  }

  const submit = async () => {
    if (resetMode) {
      if (await auth.sendPasswordReset(email)) setResetSent(true);
      return;
    }
    if (magicMode) {
      setMagicBusy(true);
      setLocalError(null);
      try {
        if (magicCompletionUrl) {
          await completeStaffMagicLogin(magicCompletionUrl, email);
          await recordCentralStaffLoginAudit("magic_link").catch(
            () => undefined,
          );
        } else {
          await requestStaffMagicLogin(email);
          setMagicSent(true);
        }
      } catch (caught) {
        setLocalError(getStaffAuthMessage(errorCode(caught), "email"));
      } finally {
        setMagicBusy(false);
      }
      return;
    }
    if (await auth.signIn(email, password)) {
      await recordCentralStaffLoginAudit("password").catch(() => undefined);
    }
  };

  return (
    <View style={styles.panel}>
      <View style={styles.copy}>
        <Text accessibilityRole="header" style={styles.title}>
          {resetMode
            ? "Reset Your Password"
            : magicCompletionUrl
              ? "Finish Secure Sign-In"
              : magicMode
                ? "Email Sign-In Link"
                : "Sign In to Central"}
        </Text>
        {resetMode ? (
          <Text style={styles.body}>We’ll email you a secure reset link.</Text>
        ) : null}
        {magicMode ? (
          <Text style={styles.body}>
            {magicCompletionUrl
              ? "Enter the staff email address that requested this link."
              : "We’ll email a one-time link. This link expires and can only be used once."}
          </Text>
        ) : null}
      </View>

      {localError || auth.error ? (
        <ErrorState
          title={magicMode || resetMode ? "Email Request Was Not Completed" : "Sign-In Was Not Completed"}
          description={localError ?? auth.error ?? "Try again."}
        />
      ) : null}

      <View style={styles.fields}>
        <Field
          autoCapitalize="none"
          autoComplete="email"
          keyboardType="email-address"
          label="Email"
          onChangeText={(value) => {
            auth.clearError();
            setLocalError(null);
            setEmail(value);
          }}
          placeholder="staff@example.com"
          inputStyle={styles.input}
          onSubmitEditing={
            resetMode || magicMode ? () => void submit() : undefined
          }
          required
          returnKeyType={resetMode || magicMode ? "done" : "next"}
          spellCheck={false}
          textContentType="emailAddress"
          value={email}
        />
        {resetMode || magicMode ? null : (
          <Field
            autoCapitalize="none"
            autoComplete="current-password"
            label="Password"
            onChangeText={(value) => {
              auth.clearError();
              setLocalError(null);
              setPassword(value);
            }}
            onSubmitEditing={() => void submit()}
            inputStyle={styles.input}
            required
            returnKeyType="done"
            secureTextEntry
            textContentType="password"
            value={password}
          />
        )}
      </View>

      {!resetMode && !magicMode ? <View style={styles.recovery}>
        <Button label="Forgot Password?" variant="link" disabled={auth.busy || magicBusy} onPress={() => {
          setResetMode(true); setPassword(""); auth.clearError(); setLocalError(null);
        }} />
      </View> : null}
      <View style={styles.actions}>
        <Button
          disabled={!email.trim() || (!resetMode && !magicMode && !password)}
          fullWidth
          label={
            resetMode
              ? "Send Reset Link"
              : magicCompletionUrl
                ? "Finish Secure Sign-In"
                : magicMode
                  ? "Email Me a Sign-In Link"
                  : "Sign In"
          }
          loading={auth.busy || magicBusy}
          onPress={() => void submit()}
          shape="rounded"
        />
        {resetMode || magicMode ? <Button
          fullWidth
          label="Back to Sign In"
          disabled={auth.busy || magicBusy}
          onPress={() => {
            setResetMode(false); setMagicMode(false); setMagicCompletionUrl(null);
            auth.clearError(); setLocalError(null);
          }}
          variant="link"
        /> : null}
        {!resetMode && !magicMode ? (
          <View style={styles.emailAlternative}>
          <Text style={styles.alternativeLabel}>Prefer to sign in without a password?</Text>
          <Button
            fullWidth
            label="Email Me a Sign-In Link"
            disabled={auth.busy || magicBusy}
            onPress={() => {
              setMagicMode(true);
              setLocalError(null);
              auth.clearError();
            }}
            shape="rounded"
            variant="link"
          />
          </View>
        ) : null}
      </View>
    </View>
  );
}

function errorCode(error: unknown) {
  if (
    typeof error === "object" &&
    error &&
    "code" in error &&
    typeof error.code === "string"
  ) {
    return error.code;
  }
  return "auth/unknown";
}

const styles = StyleSheet.create({
  panel: { gap: spacing.lg, width: "100%" },
  copy: { gap: 6 },
  title: {
    color: central.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 24,
    lineHeight: 31,
  },
  body: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 23,
  },
  fields: { gap: spacing.md },
  actions: { gap: spacing.sm },
  recovery: { flexDirection: "row", justifyContent: "flex-end", marginTop: -spacing.lg, marginBottom: -spacing.sm },
  emailAlternative: { alignItems: "center", borderTopWidth: 1, borderTopColor: central.border.default, paddingTop: spacing.md, marginTop: spacing.md, width: "100%" },
  alternativeLabel: { color: central.text.secondary, fontFamily: central.typography.body, fontSize: 13, lineHeight: 20, textAlign: "center" },
  input: {
    backgroundColor: "#ffffff",
    borderColor: central.border.default,
    borderRadius: central.radius.control,
  },
  stepTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
    lineHeight: 21,
  },
  qrFrame: {
    alignItems: "center",
    alignSelf: "center",
    backgroundColor: "#ffffff",
    borderColor: semantic.border.subtle,
    borderRadius: 12,
    borderWidth: 1,
    padding: spacing.md,
  },
  secretKey: {
    backgroundColor: semantic.surface.muted,
    borderRadius: 8,
    color: semantic.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    letterSpacing: 1.2,
    lineHeight: 20,
    padding: spacing.sm,
  },
});
